import Vue from 'vue'
import Router from 'vue-router'
import { installNavigationFailureCatch } from '@/lib/routerNavigationGuard'
import { bootIntercom } from '@/lib/analytics'
import Config from '@/config'
import UserService from '@/services/UserService'
import { setUserActiveCompanyId } from '@/store/company'
import LandingPage from './pages/LandingPage'
import WinbackWebviewPage from './pages/WinbackWebviewPage'
import LinkMissingAccountInfoPage from './pages/LinkMissingAccountInfoPage'
import InvitePage from './pages/InvitePage'

import CompanySettingsProfile from './components/Modules/Company/CompanySettings/CompanySettingsProfile'
import CompanySettingsIntegrations from './components/Modules/Company/CompanySettings/CompanySettingsIntegrations'
import CompanySettingsLoyalty from './components/Modules/Company/CompanySettings/CompanySettingsLoyalty/CompanySettingsLoyalty'
//import CompanySettingsTags from './components/Modules/Company/CompanySettings/CompanySettingsTags/CompanySettingsTags'

import LocationSettingsSurvey from './components/Modules/Location/LocationSettings/LocationSettingsSurvey'
import LocationSettingsPlatforms from './components/Modules/Location/LocationSettings/LocationSettingsPlatforms/LocationSettingsPlatforms'
import LocationSettingsDevices from './components/Modules/Location/LocationSettings/LocationSettingsDevices/LocationSettingsDevices'
import LocationSettingsIntegrations from './components/Modules/Location/LocationSettings/LocationSettingsIntegrations/LocationSettingsIntegrations'
import LocationSettingsBasic from './components/Modules/Location/LocationSettings/LocationSettingsBasic/LocationSettingsBasic'

import AccountProfileForm from '@/components/Modules/User/Account/AccountProfileForm'
import AccountNotificationSettings from '@/components/Modules/User/Account/AccountNotificationSettings'

import InsightsSummary from '@/components/Modules/Insights/SummarySection/SummaryView'
import InsightsDetails from '@/components/Modules/Insights/DetailsSection/DetailsView'

import InsightsV2Summary from '@/components/Modules/InsightsV2/SummarySection/SummaryView'
import InsightsV2Details from '@/components/Modules/InsightsV2/DetailsSection/DetailsView'

import CompetitionSummary from '@/components/Modules/Competition/SummarySection/CompetitionSummaryView'
import CompetitionDetails from '@/components/Modules/Competition/DetailsSection/CompetitionDetailsView'

Vue.use(Router)

export const routes = [
  {
    path: '/',
    name: 'landing',
    component: LandingPage,
    meta: {
      hideNav: true
    }
  },
  {
    path: '/invite/:token',
    name: 'invite',
    component: InvitePage,
    meta: {
      hideNav: true,
      public: true
    }
  },
  {
    path: '/m/winback/:conversationId',
    name: 'winback-webview',
    component: WinbackWebviewPage,
    meta: {
      hideNav: true,
      public: true
    }
  },
  {
    path: '/add-info',
    name: 'add-info',
    component: LinkMissingAccountInfoPage,
    meta: {
      hideNav: true
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    // route level code-splitting
    // this generates a separate chunk (dashboard.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "dashboard" */ './pages/DashboardPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/reviews',
    name: 'reviews',
    component: () => import(/* webpackChunkName: "review" */ './pages/ReviewsPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/loyalty',
    name: 'loyalty',
    component: () => import(/* webpackChunkName: "review" */ './pages/LoyaltyPage.vue')
  },
  {
    path: '/surveys',
    name: 'surveys',
    component: () => import(/* webpackChunkName: "survey" */ './pages/SurveysPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import(/* webpackChunkName: "customer" */ './pages/CustomersPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/customers/import',
    name: 'customers-import',
    component: () =>
      import(/* webpackChunkName: "customers-import" */ './pages/CustomersImportPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/customers/add',
    name: 'customers-add',
    component: () => import(/* webpackChunkName: "customers-add" */ './pages/CustomersAddPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/create-message',
    name: 'create-message',
    component: () =>
      import(/* webpackChunkName: "create-message" */ './pages/CreateMessagePage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/account',
    name: 'account',
    children: [
      {
        path: 'profile',
        name: 'account-profile',
        component: AccountProfileForm,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'notifications',
        name: 'account-notifications',
        component: AccountNotificationSettings,
        meta: {
          hideNav: true
        }
      }
    ],
    component: () => import(/* webpackChunkName: "user" */ './pages/AccountPage.vue')
  },
  {
    path: '/winback',
    name: 'winback',
    children: [
      {
        path: ':conversationId',
        name: 'winback-conversation',
        meta: {
          hideNav: true
        }
      }
    ],
    component: () => import(/* webpackChunkName: "winback" */ './pages/WinbackPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/campaigns',
    name: 'campaigns',
    component: () => import(/* webpackChunkName: "campaigns" */ './pages/CampaignsPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/competition',
    name: 'competition',
    children: [
      {
        path: 'summary',
        name: 'competition-summary',
        component: CompetitionSummary
      },
      {
        path: 'details',
        name: 'competition-details',
        component: CompetitionDetails
      }
    ],
    component: () => import(/* webpackChunkName: "competition" */ './pages/CompetitionPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/create-campaign',
    name: 'create-campaign',
    component: () =>
      import(/* webpackChunkName: "create-campaign" */ './pages/CreateCampaignPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/reviews-and-listing-management',
    name: 'marqii',
    component: () => import(/* webpackChunkName: "marqii" */ './pages/MarqiiPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/marqii',
    redirect: '/reviews-and-listing-management'
  },
  {
    path: '/keywords',
    name: 'keywords',
    component: () => import(/* webpackChunkName: "keywords" */ './pages/KeywordsPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/create-keyword',
    name: 'create-keyword',
    component: () =>
      import(/* webpackChunkName: "create-keyword" */ './pages/CreateKeywordPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/summary',
    redirect: '/',
    meta: {
      hideNav: true
    }
  },
  {
    path: '/insights',
    name: 'insights',
    children: [
      {
        path: 'summary',
        name: 'insights-summary',
        component: InsightsSummary,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'details',
        name: 'insights-details',
        component: InsightsDetails,
        meta: {
          hideNav: true
        }
      }
    ],
    component: () => import(/* webpackChunkName: "insights" */ './pages/InsightsPage.vue')
  },
  {
    path: '/insights/v2',
    name: 'insightsv2',
    children: [
      {
        path: 'summary',
        name: 'insights-summaryv2',
        component: InsightsV2Summary,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'details',
        name: 'insights-detailsv2',
        component: InsightsV2Details,
        meta: {
          hideNav: true
        }
      }
    ],
    component: () => import(/* webpackChunkName: "insights" */ './pages/InsightsV2Page.vue')
  },
  {
    path: '/oauth/:integrationProviderName',
    name: 'oauth',
    component: () => import(/* webpackChunkName: "oauth-page" */ './pages/OauthPage.vue'),
    meta: {
      hideNav: true
    }
  },
  {
    path: '/location-settings/:locationId',
    name: 'location-settings',
    children: [
      {
        path: 'survey',
        name: 'location-settings-survey',
        component: LocationSettingsSurvey,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'platforms',
        name: 'location-settings-platforms',
        component: LocationSettingsPlatforms,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'devices',
        name: 'location-settings-devices',
        component: LocationSettingsDevices,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'integrations',
        name: 'location-settings-integrations',
        component: LocationSettingsIntegrations,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'basic',
        name: 'location-settings-basic',
        component: LocationSettingsBasic,
        meta: {
          hideNav: true
        }
      }
    ],
    component: () => import(/* webpackChunkName: "location" */ './pages/LocationSettingsPage.vue')
  },
  {
    path: '/company-settings',
    name: 'company-settings',
    children: [
      {
        path: 'profile',
        name: 'company-settings-profile',
        component: CompanySettingsProfile,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'loyalty',
        name: 'company-settings-loyalty',
        component: CompanySettingsLoyalty,
        meta: {
          hideNav: true
        }
      },
      {
        path: 'integrations',
        name: 'company-settings-integrations',
        component: CompanySettingsIntegrations,
        meta: {
          hideNav: true
        }
      }
      // {
      //   path: 'tags',
      //   name: 'company-settings-tags',
      //   component: CompanySettingsTags,
      // },
    ],
    component: () => import(/* webpackChunkName: "company" */ './pages/CompanyPage.vue')
  },
  {
    path: '/report-builder',
    name: 'report-builder',
    component: () =>
      import(/* webpackChunkName: "report-builder" */ './pages/ReportBuilderPage.vue'),
    meta: {
      hideNav: true
    }
  }
]

installNavigationFailureCatch(Router)

const newRouter = new Router({
  mode: 'history',
  linkExactActiveClass: 'active',
  base: process.env.BASE_URL,
  routes
})

newRouter.beforeEach(async (to, from, next) => {
  if (process.env.NODE_ENV !== 'headless') {
    window.parent.postMessage({ path: to.fullPath }, Config.baseAppUrl)
  }

  next()
})

export default newRouter

export async function initRouterView(context) {
  if (window.isNativeApp && context.$route.query) {
    const { token } = context.$route.query
    if (token) {
      window.localStorage.setItem('jwt', token)
    }
  } else {
    // bootIntercom(context.$intercom)
  }

  let response
  try {
    response = await UserService.get()
  } catch (error) {
    const { status } = error
    response = { body: { notOnline: true, user: null, partner: null } }
    context.$store.dispatch('resetSettings')

    if (
      status === 401 &&
      context.$route.matched &&
      !context.$route.matched.some((record) => record.meta.public)
    ) {
      document.title = 'Login'
      return
    }
  }
  const { notOnline, user, partner } = response.body
  const { name: startRoute } = context.$route

  if (
    (notOnline && startRoute === 'landing') ||
    (!user &&
      context.$route.matched &&
      !context.$route.matched.some((record) => record.meta.public))
  ) {
    context.$store.dispatch('resetSettings')
    document.title = 'Login'
  } else if (user) {
    await context.$store.dispatch('setConfig')
    await Promise.all([
      context.$store.dispatch('user/getOnlineUser'),
      context.$store.dispatch('user/fetchAuthorizedUser')
    ])

    const needsInfo = context.$store.state.user.needsInfo

    if (process.env.NODE_ENV === 'headless' && process.env.VUE_APP_ACTIVE_COMPANY_ID) {
      setUserActiveCompanyId(
        context.$store.state.user.onlineUser._id,
        process.env.VUE_APP_ACTIVE_COMPANY_ID
      )
    } else if (context.companyId) {
      setUserActiveCompanyId(context.$store.state.user.onlineUser._id, context.companyId)
    }

    context.$store.dispatch('integrationProvider/getIntegrationProviders')
    context.$store.dispatch('dataCore/init')

    if (startRoute === 'landing' || startRoute === 'login' || startRoute === 'add-info') {
      if (needsInfo) {
        context.$router.push('add-info')
      } else {
        context.$router.push('dashboard')
      }
    }

    if (startRoute === 'invite') {
      context.$router.replace('/dashboard')
    }
  }
  context.$store.dispatch('user/fetchCompaniesUsersList')

  brandApp({ context, partner })
}

export function brandApp({ context, partner }) {
  const partnerName = partner && partner.name
  const partnerFaviconLogo = partner && partner.faviconLogo

  document.title = partnerName || context.$store.getters['user/brandName']
  document.querySelectorAll("link[rel*='icon']").forEach((node) => node.remove())

  const link = document.createElement('link')
  link.type = 'image/x-icon'
  link.rel = 'icon'
  link.href = partnerFaviconLogo || context.$store.getters['user/brandFaviconLogo']
  document.getElementsByTagName('head')[0].appendChild(link)
}

export function getUrlParameter(name) {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')
  var regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
  var results = regex.exec(window.location.search)
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}
